import { fetchImageUrlsBulk } from '../services/imageUploadService';
import { fetchImageViaProxy } from '../services/imageProxyClient';

// Shared DOM pre-processing helpers for the PDF exporters (server + raster).
//
// These used to be duplicated by hand across the PDF exporters (with a "mirror
// any change by hand" comment that was a standing future-bug magnet). They
// live here once and are imported by the exporters that need them.
// All operate on a DETACHED clone — they never mutate the live editor DOM.

// Wait for every <img> in `el` to finish loading (or error) so its src/natural
// size is resolved before serialising/snapshotting. Bounded by `timeoutMs` so a
// single stuck image can't hang the export.
export async function waitForImages(el: HTMLElement, timeoutMs = 8000): Promise<void> {
    const imgs = Array.from(el.querySelectorAll('img')) as HTMLImageElement[];
    if (imgs.length === 0) return;
    const settle = (img: HTMLImageElement) =>
        new Promise<void>((resolve) => {
            if (img.complete && img.naturalWidth > 0) return resolve();
            const done = () => {
                img.removeEventListener('load', done);
                img.removeEventListener('error', done);
                resolve();
            };
            img.addEventListener('load', done);
            img.addEventListener('error', done);
        });
    await Promise.race([Promise.all(imgs.map(settle)), new Promise<void>((resolve) => setTimeout(resolve, timeoutMs))]);
}

// Fetch a URL's bytes and return them as a base64 data: URI. Shared by both the
// raster exporter (exportToPdf.ts re-exports this) and the server inliner below
// so there is ONE implementation.
//
// The direct `credentials: 'omit'` fetch works only when the host sends CORS
// headers. That holds for most org S3 buckets (open CORS rule) but NOT all —
// some `*-aica-private-assets-prod` buckets lack it, and remote images
// (googleusercontent) never send CORS. On any such failure we fall back to the
// server-side image proxy (PDF lambda `{ url }` route), which fetches the bytes
// with no CORS restriction. Throws only when BOTH the direct fetch and the proxy
// fail, so the caller can log the gap.
export async function urlToDataUrl(url: string): Promise<string> {
    try {
        const res = await fetch(url, { credentials: 'omit' });
        if (!res.ok) throw new Error(`fetch failed: ${res.status}`);
        return await blobToDataUrl(await res.blob());
    } catch (err) {
        const viaProxy = await fetchImageViaProxy(url);
        if (viaProxy) return viaProxy;
        throw err;
    }
}

function blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

// Fetch `fetchUrl`, downscale it to `img`'s rendered size, and set the result
// as `img`'s src — the single "inline one image" step, shared by the full
// docId+remote inliner below and the server exporter's remote-URL fallback
// further down. `docId` is only for the warn log; omit it for a docId-less
// image.
async function inlineImageSrc(img: HTMLImageElement, fetchUrl: string, docId = ''): Promise<void> {
    try {
        const fullResDataUrl = await urlToDataUrl(fetchUrl);
        const dataUrl = await downscaleImageDataUrl(fullResDataUrl, renderedWidthFromContainer(img));
        img.setAttribute('src', dataUrl);
        img.removeAttribute('srcset');
    } catch (err: any) {
        const status = /fetch failed: (\d+)/.exec(String(err?.message || ''))?.[1] ?? String(err?.message || err);
        console.warn('[pdf-export] image inline failed', { docId: docId || undefined, src: fetchUrl, status });
    }
}

// Inline every <img> as a base64 data: URI. Required by the server exporter: the
// Lambda blocks all non-data:/about: network fetches during render
// (PdfHandler.renderPdf request interception), so an <img> still pointing at a
// presigned S3 URL is aborted and renders blank. Done on the detached clone so
// the live editor is untouched.
//
// This mirrors the proven raster path (exportToPdf.ts resolveDocIdImagesToBase64):
// it walks the DURABLE `data-doc-id` set — NOT the live `<img src>` — and re-mints
// FRESH presigned URLs at export time. The presigned URL baked into the live DOM
// at editor-mount is cached only ~10 min, so by export time it is usually EXPIRED
// (403). Re-minting makes the inline robust to expiry, and walking the docId set
// (not the rendered `<img>` set) also dodges the placeholder race. Images with no
// `data-doc-id` (unsaved base64 images — already data:, or a remote non-docId URL)
// fall back to fetching their live `src` directly.
export async function inlineImagesToDataUris(root: HTMLElement): Promise<void> {
    const imgs = Array.from(root.querySelectorAll('img')) as HTMLImageElement[];

    // 1. Re-mint fresh presigned URLs for every persisted (docId-backed) image.
    const docIdSet = new Set<string>();
    imgs.forEach((img) => {
        const id = img.getAttribute('data-doc-id');
        if (id) docIdSet.add(id);
    });

    let freshUrlByDocId: Record<string, string> = {};
    if (docIdSet.size > 0) {
        freshUrlByDocId = await fetchImageUrlsBulk(Array.from(docIdSet));
    }

    // 2. Resolve each image to a data: URI: prefer the fresh presigned URL minted
    //    from data-doc-id; fall back to the live src for non-docId images.
    await Promise.all(
        imgs.map(async (img) => {
            const docId = img.getAttribute('data-doc-id') || '';
            const liveSrc = img.getAttribute('src') || '';

            // An unsaved base64 image is already a data: URI — nothing to inline.
            if (!docId && liveSrc.startsWith('data:')) return;

            const fetchUrl = docId ? freshUrlByDocId[docId] : liveSrc;
            if (!fetchUrl || !/^https?:/i.test(fetchUrl)) {
                if (docId) {
                    console.warn('[pdf-export] image inline failed', {
                        docId,
                        src: liveSrc,
                        status: 'no presigned URL',
                    });
                }
                return;
            }

            await inlineImageSrc(img, fetchUrl, docId);
        }),
    );
}

// ── Server-render image resolution (RECUR-28015 payload-size fix) ───────────
//
// For a docId-backed image the server exporter re-mints a fresh presigned URL
// and leaves it on the <img> — no downscale, no byte fetch — so the render
// Lambda fetches it itself (in-region, in parallel) instead of the browser
// downloading, base64-ing (+33%), and re-uploading every image. The bulk
// fetch always bypasses the cache: mnemosyne's constant name claims these
// URLs are valid ~120s, but its signer has a unit bug that actually grants
// ~120 minutes — since neither number is safe to assume stays true, the
// export mints a URL immediately before use rather than trusting the
// editor's 10-minute cache.
//
// A docId-less image can't take that shortcut: the Lambda blocks any fetch
// that isn't data:/about: or an allowlisted presigned S3 URL, so a plain
// http(s) <img src> (e.g. one pasted from the web) would render blank. Those
// are inlined here via the same fetch+downscale path as the raster inliner
// (`inlineImageSrc`); an already-`data:` src (an unsaved paste) needs no work
// either way.
export async function resolveImagesToPresignedUrls(root: HTMLElement): Promise<void> {
    const imgs = Array.from(root.querySelectorAll('img')) as HTMLImageElement[];

    const docIdSet = new Set<string>();
    imgs.forEach((img) => {
        const id = img.getAttribute('data-doc-id');
        if (id) docIdSet.add(id);
    });
    const freshUrlByDocId = docIdSet.size > 0 ? await fetchImageUrlsBulk(Array.from(docIdSet), true) : {};

    await Promise.all(
        imgs.map(async (img) => {
            const docId = img.getAttribute('data-doc-id');
            if (docId) {
                const fresh = freshUrlByDocId[docId];
                if (!fresh || !/^https?:/i.test(fresh)) {
                    // Leave the stale src rather than blanking it: the Lambda aborts a
                    // non-allowlisted fetch and renders a gap either way, but keeping the
                    // attribute preserves layout and makes the failure legible in the log.
                    console.warn('[pdf-export] no presigned URL for image', { docId });
                    return;
                }
                img.setAttribute('src', fresh);
                img.removeAttribute('srcset');
                return;
            }

            const liveSrc = img.getAttribute('src') || '';
            if (liveSrc.startsWith('data:')) return;
            if (/^https?:/i.test(liveSrc)) await inlineImageSrc(img, liveSrc);
        }),
    );
}

// ── Downscaling oversized inlined images (RECUR-27304 payload-size fix) ──────
//
// An image inlined at its ORIGINAL resolution regardless of how small it
// actually renders in the doc (e.g. a 4000px-wide photo shown at 300px) is a
// major, needless contributor to a media-heavy One Pager's export payload
// blowing past the render Lambda's ~6MB sync-request limit (see
// exportToPdfServer.ts's non-PDF-response guard for the other half of this
// fix). Cap each image's inlined resolution to (at most) 2x its rendered
// width in the doc — generous headroom for retina displays — before
// base64-ing it.

/** Generic ceiling used when the image's rendered width can't be determined
 *  from the clone (an unconstrained/natural-size image) — wide enough for
 *  any real editor column at typical zoom, so it only ever kicks in for
 *  genuinely oversized source images. */
export const UNKNOWN_RENDERED_WIDTH_CAP_PX = 2464;
const RENDERED_WIDTH_HEADROOM_MULTIPLIER = 2;

export interface ImageDownscaleTarget {
    width: number;
    height: number;
}

/**
 * Pure decision math (no canvas/DOM) for whether/how to downscale an image
 * before inlining: `null` when the image is already at or under the cap (no
 * downscale needed), otherwise the target size with aspect ratio preserved.
 * Exported so this is unit-testable without a canvas-capable environment.
 */
export function computeImageDownscaleTarget(
    naturalWidth: number,
    naturalHeight: number,
    renderedWidth: number | null | undefined,
): ImageDownscaleTarget | null {
    const cap =
        renderedWidth != null && renderedWidth > 0 ? renderedWidth * RENDERED_WIDTH_HEADROOM_MULTIPLIER : UNKNOWN_RENDERED_WIDTH_CAP_PX;
    if (!(naturalWidth > cap)) return null;
    const scale = cap / naturalWidth;
    return { width: Math.round(cap), height: Math.round(naturalHeight * scale) };
}

// The rendered CSS width (px) ImageElement paints on its container's inline
// `width` style — set only when the user has resized the image (see
// ImageElement.tsx); this survives `cloneNode`, so it's readable straight off
// the detached clone without any live-DOM cross-reference. `null` when unset
// (the image renders at its natural size), so the caller falls back to the
// fixed generic cap above.
function renderedWidthFromContainer(img: HTMLImageElement): number | null {
    const width = parseFloat(img.parentElement?.style.width || '');
    return Number.isFinite(width) && width > 0 ? width : null;
}

function dataUrlMimeType(dataUrl: string): string {
    return /^data:([^;,]+)/.exec(dataUrl)?.[1] || 'image/png';
}

function decodeImage(dataUrl: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('failed to decode image for downscale'));
        img.src = dataUrl;
    });
}

const DOWNSCALE_ENCODE_QUALITY = 0.9; // only affects lossy encodings (jpeg/webp) — a no-op for png.

/**
 * Downscales `dataUrl` to `computeImageDownscaleTarget`'s decision, via an
 * offscreen canvas. ANY failure (decode error, canvas taint/SecurityError,
 * `toDataURL` throwing) falls back to the ORIGINAL full-res `dataUrl`
 * unchanged — a bigger payload is always safer than a broken/blank image.
 */
async function downscaleImageDataUrl(dataUrl: string, renderedWidth: number | null): Promise<string> {
    try {
        const img = await decodeImage(dataUrl);
        const target = computeImageDownscaleTarget(img.naturalWidth, img.naturalHeight, renderedWidth);
        if (!target) return dataUrl;

        const canvas = document.createElement('canvas');
        canvas.width = target.width;
        canvas.height = target.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return dataUrl;
        ctx.drawImage(img, 0, 0, target.width, target.height);

        return canvas.toDataURL(dataUrlMimeType(dataUrl), DOWNSCALE_ENCODE_QUALITY) || dataUrl;
    } catch {
        return dataUrl;
    }
}

export function stripInteractiveChrome(root: HTMLElement) {
    const selectors = [
        '[data-resize-handle]',
        '[class*="ResizeHandle"]',
        '[class*="FloatingToolbar"]',
        '[data-slate-placeholder]',
        '[data-plate-toolbar]',
        // Image editor chrome — the wrap toolbar, the "Uploading…" chip / error
        // overlay, the resize handles, and the delete (×) button must never
        // appear in the export.
        '[class*="ImageWrapMenu"]',
        '[class*="ImageElement_uploadChip"]',
        '[class*="ImageElement_uploadError"]',
        '[class*="ImageElement_handle"]',
        '[class*="ImageElement_deleteBtn"]',
    ];
    for (const sel of selectors) {
        root.querySelectorAll(sel).forEach((n) => n.remove());
    }
    // Drop transient selection classes so their blue ::after / ring overlay
    // can't render: the cell-selection tint AND the image selection border.
    root.querySelectorAll('[class*="cellSelected"], [class*="imageContainerSelected"]').forEach((el) => {
        const kept = Array.from((el as HTMLElement).classList).filter((c) => !/cellSelected|imageContainerSelected/i.test(c));
        (el as HTMLElement).className = kept.join(' ');
    });
}

// Per-column pixel widths of a LIVE (laid-out) table, read from its rendered
// cells. Prefers a row of all colSpan=1 cells — those cell boxes ARE the column
// boxes; if every row has merges, the first row's cells are split equally
// across their spans. Returns null when the table has no layout (jsdom,
// display:none) so callers can fall back to the colgroup-derived conversion.
function measureRenderedColumnWidths(table: HTMLTableElement): number[] | null {
    const rows = Array.from(table.rows);
    if (!rows.length) return null;
    const spanCount = (row: HTMLTableRowElement) => Array.from(row.cells).reduce((n, c) => n + (c.colSpan || 1), 0);
    const colCount = Math.max(...rows.map(spanCount));
    if (!colCount) return null;
    for (const row of rows) {
        const cells = Array.from(row.cells);
        if (cells.length === colCount && cells.every((c) => (c.colSpan || 1) === 1)) {
            const widths = cells.map((c) => c.getBoundingClientRect().width);
            return widths.every((w) => w > 0) ? widths : null;
        }
    }
    const widths: number[] = [];
    for (const cell of Array.from(rows[0].cells)) {
        const span = cell.colSpan || 1;
        const w = cell.getBoundingClientRect().width / span;
        for (let i = 0; i < span; i++) widths.push(w);
    }
    return widths.length === colCount && widths.every((w) => w > 0) ? widths : null;
}

// Make each exported table reproduce the column widths the user actually SEES.
//
// Preferred path (renderedSource supplied — the live editor content the clone
// was made from, paired to the clone's tables by document order): MEASURE each
// column from the laid-out cells and write percentages onto the clone,
// creating the <colgroup> when the table has none. This is the only faithful
// source of truth: the editor renders `table-layout: fixed; width: 100%`, and
// @platejs/table's setTableColSize stores PARTIAL colSizes (a single column
// drag yields [520, 0, 0]) whose zero entries render as bare <col> that soak
// up the LEFTOVER width. Deriving widths from the colgroup alone (the old
// average-fill) collapsed such tables to equal columns in the PDF (RECUR-26249).
//
// Fallback (no renderedSource, or a source without layout): convert the
// colgroup's px widths to percentages of their sum — proportions survive the
// narrower page, zero-width columns get the average of the defined ones.
export function normalizeTableColumnsForPrint(root: HTMLElement, renderedSource?: HTMLElement) {
    const tables = Array.from(root.querySelectorAll('table'));
    const sourceTables = renderedSource ? Array.from(renderedSource.querySelectorAll('table')) : [];
    tables.forEach((table, index) => {
        const source = sourceTables[index];
        const measured = source ? measureRenderedColumnWidths(source as HTMLTableElement) : null;
        const measuredTotal = measured ? measured.reduce((sum, w) => sum + w, 0) : 0;
        if (measured && measuredTotal > 0) {
            table.querySelector('colgroup')?.remove();
            const colgroup = document.createElement('colgroup');
            measured.forEach((w) => {
                const col = document.createElement('col');
                col.style.width = `${((w / measuredTotal) * 100).toFixed(4)}%`;
                colgroup.appendChild(col);
            });
            table.insertBefore(colgroup, table.firstChild);
            return;
        }

        const cols = Array.from(table.querySelectorAll('colgroup > col')) as HTMLElement[];
        if (cols.length === 0) return;
        const widths = cols.map((col) => {
            const styleW = parseFloat(col.style.width || '');
            const attrW = parseFloat(col.getAttribute('width') || '');
            const w = Number.isFinite(styleW) && styleW > 0 ? styleW : Number.isFinite(attrW) && attrW > 0 ? attrW : 0;
            return w;
        });
        const definedTotal = widths.reduce((sum, w) => sum + w, 0);
        if (definedTotal <= 0) return;
        const definedCount = widths.filter((w) => w > 0).length;
        const avg = definedTotal / Math.max(definedCount, 1);
        const filled = widths.map((w) => (w > 0 ? w : avg));
        const total = filled.reduce((sum, w) => sum + w, 0);
        cols.forEach((col, i) => {
            const pct = (filled[i] / total) * 100;
            col.style.width = `${pct.toFixed(4)}%`;
            col.removeAttribute('width');
        });
    });
}

// Hide trailing empty paragraphs (TrailingBlockPlugin's caret-landing spot + any
// stray user-pressed-Enter empties at the document end) so the final page isn't
// one or two lines of whitespace.
export function trimTrailingEmptyBlocks(root: HTMLElement) {
    const blocks = Array.from(root.children).filter((c) => c instanceof HTMLElement) as HTMLElement[];
    while (blocks.length > 0) {
        const last = blocks[blocks.length - 1];
        const text = (last.textContent || '').trim();
        const hasMedia = last.querySelector('img, video, table, figure, iframe, hr');
        if (text.length === 0 && !hasMedia) {
            last.remove();
            blocks.pop();
        } else {
            break;
        }
    }
}

const MEDIA_SEL = 'img, video, table, figure, iframe, hr, svg, canvas';

// Remove trailing whitespace-only nodes. The editor stores the user's trailing
// blank lines as text nodes full of "\n"; under `white-space: pre-wrap` these
// render as tall empty space that spills into a blank final PDF page. We descend
// the last-child chain dropping pure-whitespace text nodes and empty inline
// wrappers, then trim trailing whitespace off the final text run.
//
// `hasMedia` matches the element ITSELF as well as its descendants. The self-match
// is essential: the descent can reach a bare <img> (a void image as the document's
// last block, once trimTrailingEmptyBlocks has dropped the trailing empty
// paragraph). querySelector only checks descendants, so without matches() the
// childless <img> looked "empty" and was removed — silently dropping a trailing
// image from the exported PDF. (Adding any element after the image moved the
// descent off the <img>, which is why a trailing image failed but image+text did
// not.)
export function trimTrailingWhitespace(root: HTMLElement) {
    const hasMedia = (el: Element) => el.matches(MEDIA_SEL) || el.querySelector(MEDIA_SEL) !== null;
    let node: Node | null = root;
    let guard = 0;
    while (node && guard++ < 100000) {
        const last: ChildNode | null = node.lastChild;
        if (!last) break;
        if (last.nodeType === Node.TEXT_NODE) {
            const text = last.textContent || '';
            if (!text.trim()) {
                last.remove();
                continue;
            }
            last.textContent = text.replace(/\s+$/, '');
            break;
        }
        if (last.nodeType === Node.ELEMENT_NODE) {
            const el = last as HTMLElement;
            if (!(el.textContent || '').trim() && !hasMedia(el)) {
                el.remove();
                continue;
            }
            node = el; // descend into the last non-empty element and keep trimming
            continue;
        }
        last.remove(); // comments / other
    }
}
